/**
 * @module api/domain/repair
 * @description 수리 접수/현황/이력 관련 API
 *   - 접수 요청 CRUD, 접수 현황 관리, 수리 완료 상세
 *   - 수리 유형/부품/방문 시간 조회
 *   - 바이패스(모바일 고객용) API
 *   - 모바일 수리파트너(인증된 staff-team) API
 */
import { getApi, api, deleteApi, noCustomApi, bypassGetApi, bypassApi } from '@/api/api'
import { buildQueryString } from '@/mixin/mixins'

// ──────────────────────────────────────────────
// 접수 요청
// ──────────────────────────────────────────────

/** 접수 이력 존재 여부 조회 */
export const getRepairRequestExistence = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-request/customer' + queryString
  return await getApi(url)
}

/** 상담에서 접수로 넘어갈 때 customerId로 고객 조회 */
export const getRepairRequestCustomerId = async (customerId) => {
  const url = `/repair/customer/${customerId}`
  return await getApi(url)
}

/** 이전 접수 이력 조회 */
export const getRepairRequestHistory = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-request/customer/pickup' + queryString
  return await getApi(url)
}

/** 이전 접수 상세 조회 (고객 기준) */
export const getRepairRequestHistoryDetailByCustomer = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-request/history' + queryString
  return await getApi(url)
}

/** 접수 이력 상세 데이터 조회 */
export const getRepairRequestHistoryDetail = async (requestId) => {
  const url = `/repair-request/${requestId}`
  return await getApi(url)
}

/** 접수 등록 제품군 조회 팝업 */
export const getProductLineList = async (partnerId) => {
  const url = '/repair-request/product-model/code_all?partnerId=' + partnerId
  return await getApi(url)
}

/** 접수 등록 제품 조회 팝업 */
export const getProductModelList = async (page, size, queryObject) => {
  const queryString = buildQueryString({ page, size, ...queryObject })

  const url = '/repair-request/product' + queryString
  return await getApi(url)
}

/** 접수 이력 메모 팝업 조회 */
export const getMemoDetail = async (repairRequestId) => {
  const url = `/repair-request/memo/${repairRequestId}`
  return await getApi(url)
}

/** 접수 등록 */
export const saveRepairRequest = async (method, data) => {
  const url = '/repair-request'
  return await api(url, method, data)
}

/** 메모 저장 */
export const updateRepairRequestMemo = async (repairRequestId, method, data) => {
  const url = `/repair-request/memo/${repairRequestId}`
  return await api(url, method, data)
}

/** 접수 이력 상세 수정 */
export const updateRepairRequestHistoryDetail = async (repairRequestId, method, data) => {
  const url = `/repair-request/${repairRequestId}`
  return await api(url, method, data)
}

/** 접수 이력 상세 삭제 */
export const deleteRepairRequestHistoryDetail = async (repairRequestId, method, data) => {
  const url = `/repair-request/${repairRequestId}`
  return await deleteApi(url, method, data)
}

// ──────────────────────────────────────────────
// 접수 현황 관리
// ──────────────────────────────────────────────

/** 상태별 건수 조회 */
export const getRepairRequestStatusCount = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/status-count' + queryString
  return await getApi(url)
}

/** 접수 현황 관리 상세 조회 */
export const getRepairRequestManagementDetail = async (repairRequestId) => {
  const url = `/repair-management/${repairRequestId}`
  return await getApi(url)
}

/** 지역에 맞는 수리파트너 직원 조회 */
export const getStaffByArea = async (staffTeamId, areaMainCode, areaSubCode) => {
  const url = `/repair-management/${staffTeamId}/exists?areaMainCode=${areaMainCode}&areaSubCode=${areaSubCode}`
  return await getApi(url)
}

/** 접수 완료 가능 여부 조회 */
export const getRepairRequestCompletedValid = async (repairStaffId, repairRequestId) => {
  const url = `/repair-management/intermediate/valid/${repairStaffId}/${repairRequestId}`
  return await getApi(url)
}

/** 대표 수리기사 조회 */
export const getRepairRequestRepresentativeStaff = async (repairStaffId, repairRequestId) => {
  const url = '/repair-management/representative'
  return await getApi(url)
}

/** 접수 현황 관리 상세 수정 (접수 지역에 기사가 있는 경우) */
export const updateRepairRequestManagementDetail = async (repairRequestId, method, data) => {
  const url = `/repair-management/${repairRequestId}`
  return await api(url, method, data)
}

/** 접수 현황 관리 상세 수정 (접수 지역에 기사가 없는 경우) */
export const updateRepairRequestManagementDetailRepresentative = async (repairRequestId, method, data) => {
  const url = `/repair-management/representative/${repairRequestId}`
  return await api(url, method, data)
}

/** 접수 상태 배정에서 방문 예정 처리 */
export const saveRequestStatusAssignmentToVisit = async (repairRequestStatusType, method, data) => {
  const url = '/repair-management/register/pre?repairRequestStatusType=' + repairRequestStatusType
  return await noCustomApi(url, method, data)
}

/** 접수 상태 방문 예정에서 완료 처리 */
export const saveRequestStatusVisitToCompleted = async (method, data) => {
  const url = '/repair-management/register/completed'
  // SURINP-480: 이미 완료된 접수의 재제출(ALREADY_COMPLETED_REPAIR_REQUEST)을 호출부가 구분해야 하므로
  // 실패 응답의 에러 코드를 노출한다. 에러 토스트는 인터셉터가 그대로 표시한다.
  return await api(url, method, data, { returnErrorResponse: true })
}

/** 접수 완료 시 비용 변경 저장 */
export const updateCompletedRequestModifyCost = async (repairRequestId, method, data) => {
  const url = `/repair-management/cost/${repairRequestId}`
  return await api(url, method, data)
}

/** 수리 완료 상세 - 배송 정보 수정 */
export const updateRepairRequestDelivery = async (method, repairRequestId, data) => {
  const url = `/repair-management/${repairRequestId}/delivery`
  return await api(url, method, data)
}

// ──────────────────────────────────────────────
// 수리 이력 / 완료 상세
// ──────────────────────────────────────────────

/** 접수 현황 완료 상세 조회 1 */
export const getRepairRequestCompletedDetail1 = async (repairRequestId) => {
  const url = `/repair-history/${repairRequestId}`
  return await getApi(url)
}

/** 접수 현황 완료 상세 조회 2 */
export const getRepairRequestCompletedDetail2 = async (repairRequestId) => {
  const url = `/repair-history/${repairRequestId}/reports`
  return await getApi(url)
}

// ──────────────────────────────────────────────
// 수리 완료 상세 (바이패스 - 수리기사용)
// ──────────────────────────────────────────────

/** 모바일 수리기사 완료 상세 조회 1 (바이패스) */
export const getBypassCompletedDetail1 = async (repairRequestId, token) => {
  const url = `/bypass/repair-history/${repairRequestId}?token=${token}`
  return await bypassGetApi(url)
}

/** 모바일 수리기사 완료 상세 조회 2 - 리포트 (바이패스) */
export const getBypassCompletedDetail2 = async (repairRequestId, token) => {
  const url = `/bypass/repair-history/${repairRequestId}/reports?token=${token}`
  return await bypassGetApi(url)
}

// ──────────────────────────────────────────────
// 수리 유형 / 부품 / 방문 시간
// ──────────────────────────────────────────────

/** 수리 유형 SUB 조회 */
export const getBrokenSubType = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/broken-type/sub' + queryString
  return await getApi(url)
}

/** 수리 유형 MAIN 조회 */
export const getBrokenMainType = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/broken-type/main' + queryString
  return await getApi(url)
}

/** 부품 MAIN 타입 조회 */
export const getPartMainType = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/product-part-type/main' + queryString
  return await getApi(url)
}

/** 부품 SUB 타입 조회 */
export const getPartSubType = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/product-part-type/sub' + queryString
  return await getApi(url)
}

/** 부품 조회 */
export const getPart = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/product-part' + queryString
  return await getApi(url)
}

/** 방문 시간 리스트 조회 */
export const getVisitTimeList = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/repair-management/visit-time' + queryString
  return await getApi(url)
}

/** 수리 완료 팝업 데이터 조회 */
export const getIntermediate = async (repairStaffId, requestId) => {
  const url = `/repair-management/intermediate/${repairStaffId}/${requestId}`
  return await getApi(url)
}

// ──────────────────────────────────────────────
// 바이패스 (모바일 고객용)
// ──────────────────────────────────────────────

/** 모바일 방문 예정 시간 데이터 조회 (바이패스) */
export const getBypassVisitTimeList = async (token) => {
  const url = '/bypass/repair-request/propose/visit-time?token=' + token
  return await bypassGetApi(url)
}

/** 모바일 방문 일자 설정 가능 여부 조회 (바이패스) */
export const getAvailabilityVisitDateTime = async (repairRequestId, token) => {
  const url = `/bypass/repair-request/${repairRequestId}/visit/assignable?token=` + token
  return await bypassGetApi(url)
}

/** 모바일 접수 내역 상세 조회 (바이패스) */
export const getRequestDetail = async (repairRequestId, token) => {
  const url = `/bypass/repair-request/${repairRequestId}/visit?token=` + token
  return await bypassGetApi(url)
}

/** 모바일 방문 일정 조회 (바이패스) */
export const getVisitDatePropose = async (repairRequestId, token) => {
  const url = `/bypass/repair-request/${repairRequestId}/propose?token=` + token
  return await bypassGetApi(url)
}

/** 모바일 방문 일정 등록 (바이패스) */
export const saveVisitDate = async (repairRequestId, token, data) => {
  const url = `/bypass/repair-request/${repairRequestId}/propose?token=` + token
  return await bypassApi(url, 'put', data)
}

/** 모바일 방문 일정 확정 (바이패스) */
export const saveVisitDateConfirm = async (visitScheduleLogId, token) => {
  const url = `/bypass/repair-request/${visitScheduleLogId}/propose?token=` + token
  return await bypassApi(url, 'post', visitScheduleLogId)
}

/** 모바일 접수 진행/완료 횟수 조회 (바이패스) */
export const getRequestCount = async (repairStaffId, queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = `/bypass/repair-request/repair-staff/count/${repairStaffId}` + queryString
  return await bypassGetApi(url)
}

/** 모바일 제품군 리스트 조회 (바이패스) */
export const getMobileProductModelList = async (repairStaffId, token) => {
  const url = `/bypass/repair-request/repair-staff/product-model/${repairStaffId}?token=${token}`
  return await bypassGetApi(url)
}

/** 모바일 접수 이력 조회 (바이패스) */
export const getHistoryList = async (repairStaffId, queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = `/bypass/repair-request/repair-staff/history/${repairStaffId}` + queryString
  return await bypassGetApi(url)
}

/** 접수 요청 처리 상태 변경 (바이패스) */
export const saveRequestProcessTypeChange = async (repairRequestId, token, method, data) => {
  const url = `/bypass/repair-request/${repairRequestId}/cancel?token=${token}`
  return await bypassApi(url, method, data)
}

/** 모바일 접수 건 택배 조회 (바이패스) */
export const getRequestDeliveryDetail = async (repairRequestId, token) => {
  const url = `/bypass/repair-request/${repairRequestId}/delivery?token=${token}`
  return await bypassGetApi(url)
}

/** 급지 리스트 조회 (바이패스) */
export const getVisitDifficultyByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/visit-difficulty-type/all' + queryString
  return await bypassGetApi(url)
}

/** 출장비 리스트 조회 (바이패스) — SURINP-192 */
export const getRepairTravelFeeByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/repair-travel-fee/all' + queryString
  return await bypassGetApi(url)
}

/** 수리 유형 SUB 조회 (바이패스) */
export const getBrokenSubTypeByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/broken-type/sub' + queryString
  return await bypassGetApi(url)
}

/** 수리 유형 MAIN 조회 (바이패스) */
export const getBrokenMainTypeByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/broken-type/main' + queryString
  return await bypassGetApi(url)
}

/** 부품 MAIN 타입 조회 (바이패스) */
export const getPartMainTypeByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/product-part-type/main' + queryString
  return await bypassGetApi(url)
}

/** 부품 SUB 타입 조회 (바이패스) */
export const getPartSubTypeByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/product-part-type/sub' + queryString
  return await bypassGetApi(url)
}

/** 부품 조회 (바이패스) */
export const getPartByPass = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/bypass/repair-management/product-part' + queryString
  return await bypassGetApi(url)
}

/** 접수 완료 저장 1 - 방문 예정 처리 (바이패스) */
export const saveRepairReportPre = async (repairRequestStatusType, data, token) => {
  const url = `/bypass/repair-management/register/pre?token=${token}&repairRequestStatusType=${repairRequestStatusType}`
  return await bypassApi(url, 'post', data)
}

/** 접수 완료 저장 2 - 완료 처리 (바이패스) */
export const saveRepairReportCompleted = async (data, token) => {
  const url = `/bypass/repair-management/register/completed?token=${token}`
  return await bypassApi(url, 'post', data)
}

// ──────────────────────────────────────────────
// 모바일 수리파트너 (인증된 staff-team)
// ──────────────────────────────────────────────

/** 모바일 수리파트너 접수 건수 조회 */
export const getStaffTeamRequestCount = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/mobile/staff-team/repair-management/count' + queryString
  return await getApi(url)
}

/** 모바일 수리파트너 제품군 리스트 조회 */
export const getStaffTeamProductModelList = async () => {
  const url = '/mobile/staff-team/repair-management/product-model'
  return await getApi(url)
}

/** 모바일 수리파트너 접수 이력 리스트 조회 */
export const getStaffTeamHistoryList = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/mobile/staff-team/repair-management/history' + queryString
  return await getApi(url)
}

/** 모바일 수리파트너 자동 배정 리스트 조회 */
export const getStaffTeamAutoAssignmentList = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/mobile/staff-team/auto-assignment' + queryString
  return await getApi(url)
}

/** 모바일 수리파트너 접수 완료 가능 여부 조회 */
export const getStaffTeamRepairCompletedValid = async (repairStaffId, repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/intermediate/valid/${repairStaffId}/${repairRequestId}`
  return await getApi(url)
}

/** 모바일 수리파트너 중간 수리 데이터 조회 */
export const getStaffTeamIntermediate = async (repairStaffId, repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/intermediate/${repairStaffId}/${repairRequestId}`
  return await getApi(url)
}

/** 모바일 수리파트너 접수 상세(고객/제품/방문 정보 등) 조회 — RepairRequestVisitProposeResponse 반환 */
export const getStaffTeamRequestVisitDetail = async (repairStaffId, repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit-detail/${repairStaffId}`
  return await getApi(url)
}

/** 모바일 수리파트너 수리 유형 MAIN 조회 */
export const getStaffTeamBrokenMainType = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/broken-type/main`
  return await getApi(url)
}

/** 모바일 수리파트너 수리 유형 SUB 조회 */
export const getStaffTeamBrokenSubType = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/broken-type/sub`
  return await getApi(url)
}

/** 모바일 수리파트너 부품 MAIN 타입 조회 */
export const getStaffTeamPartMainType = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/product-part-type/main`
  return await getApi(url)
}

/** 모바일 수리파트너 부품 SUB 타입 조회 */
export const getStaffTeamPartSubType = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/product-part-type/sub`
  return await getApi(url)
}

/** 모바일 수리파트너 부품 리스트 조회 */
export const getStaffTeamPart = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/product-part`
  return await getApi(url)
}

/** 모바일 수리파트너 방문 시간 리스트 조회 */
export const getStaffTeamVisitTimeList = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit-time`
  return await getApi(url)
}

/** 모바일 수리파트너 출장비 리스트 조회 (SURINP-192) */
export const getStaffTeamRepairTravelFeeAll = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/repair-travel-fee/all`
  return await getApi(url)
}

/** 모바일 수리파트너 급지 리스트 조회 */
export const getStaffTeamVisitDifficultyAll = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit-difficulty-type/all`
  return await getApi(url)
}

/** 모바일 수리파트너 방문 예정 등록 */
export const saveStaffTeamRepairReportPre = async (repairRequestId, repairRequestStatusType, data) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/register/pre?repairRequestStatusType=${repairRequestStatusType}`
  return await noCustomApi(url, 'post', data)
}

/** 모바일 수리파트너 완료 등록 */
export const saveStaffTeamRepairReportCompleted = async (repairRequestId, data) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/register/completed`
  return await noCustomApi(url, 'post', data)
}

/** 모바일 수리파트너 접수 방문 상세 조회 (이관/취소용) */
export const getStaffTeamRepairRequestVisit = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit`
  return await getApi(url)
}

/** 모바일 수리파트너 접수 취소 처리 */
export const cancelStaffTeamRepairRequest = async (repairRequestId, data) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/cancel`
  return await noCustomApi(url, 'post', data)
}

/** 모바일 수리파트너 방문 일정 상세 조회 (접수 상태) */
export const getStaffTeamVisitDateDetail = async (repairRequestId, repairStaffId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/detail/${repairStaffId}`
  return await getApi(url)
}

/** 모바일 수리파트너 방문 일정 상세 조회 (직원 로그인 후) */
export const getStaffTeamVisitDateDetail2 = async (repairRequestId, repairStaffId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit-detail/${repairStaffId}`
  return await getApi(url)
}

/** 모바일 수리파트너 방문 시간 리스트 조회 (직원 기준) */
export const getStaffTeamVisitTimeListByStaff = async (repairStaffId) => {
  const url = `/mobile/staff-team/repair-management/propose/visit-time/${repairStaffId}`
  return await getApi(url)
}

/** 모바일 수리파트너 방문일정 등록 조회 */
export const getStaffTeamVisitDateRegistration = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/propose`
  return await getApi(url)
}

/** 모바일 수리파트너 방문일정 등록 저장 */
export const saveStaffTeamVisitDate = async (repairRequestId, data) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/propose`
  return await noCustomApi(url, 'put', data)
}

/** 모바일 수리파트너 방문일정 확정 */
export const saveStaffTeamVisitDateConfirm = async (visitScheduleLogId, repairStaffId) => {
  const url = `/mobile/staff-team/repair-management/${visitScheduleLogId}/propose/${repairStaffId}`
  return await noCustomApi(url, 'post')
}

/** 모바일 수리기사 방문일정 변경 상세 조회 (바이패스) */
export const getBypassVisitModifyDetail = async (repairRequestId, token) => {
  const url = `/bypass/repair-request/${repairRequestId}/visit/modify?token=${token}`
  return await bypassGetApi(url)
}

/** 모바일 수리기사 방문일정 변경 (바이패스) */
export const modifyBypassVisitDate = async (repairRequestId, token, data) => {
  const url = `/bypass/repair-request/${repairRequestId}/visit/modify?token=${token}`
  return await bypassApi(url, 'post', data)
}

/** 모바일 수리파트너 방문일정 변경 상세 조회 */
export const getStaffTeamVisitModifyDetail = async (repairRequestId, repairStaffId) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit/modify/${repairStaffId}`
  return await getApi(url)
}

/** 모바일 수리파트너 방문일정 변경 */
export const modifyStaffTeamVisitDate = async (repairRequestId, repairStaffId, data) => {
  const url = `/mobile/staff-team/repair-management/${repairRequestId}/visit/modify/${repairStaffId}`
  return await noCustomApi(url, 'post', data)
}

/** 모바일 수리파트너 완료 상세 조회 1 */
export const getStaffTeamCompletedDetail1 = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-history/${repairRequestId}`
  return await getApi(url)
}

/** 모바일 수리파트너 완료 상세 조회 2 - 리포트 */
export const getStaffTeamCompletedDetail2 = async (repairRequestId) => {
  const url = `/mobile/staff-team/repair-history/${repairRequestId}/reports`
  return await getApi(url)
}

// ──────────────────────────────────────────────
// 수리 액션 (RepairAction) — 경로 정보 (SURINP-223)
// ──────────────────────────────────────────────

/**
 * 수리 액션 경로 정보 PATCH — bypass(수리기사 카카오톡 토큰) 인증.
 * 일반 JWT가 없는 모바일 수리기사 화면(`/mobile/staff/request-completed`)에서 사용.
 */
export const patchRepairActionRouteBypass = async (repairActionId, token, payload) => {
  try {
    const url = `/bypass/repair-action/${repairActionId}/route?token=${token}`
    // 성공/실패 메시지는 호출부(saveRouteFromGps)에서 일괄 처리하므로 자동 토스트를 쓰지 않는다.
    // 성공 시에만 응답을 반환하고, 그 외에는 false 로 정규화한다.
    const { status, data } = await bypassApi(url, 'patch', payload)
    return status === 200 ? { status, data } : false
  } catch (e) {
    window.__APP_LOG__('[repair] patchRepairActionRouteBypass error:', e)
    return false
  }
}

/**
 * 수리 액션 경로 정보 PATCH — staff-team 세션 JWT 인증.
 * 모바일 수리파트너 화면(`/mobile/staff-team/request-completed`)에서 사용.
 */
export const patchRepairActionRouteStaffTeam = async (repairActionId, payload) => {
  try {
    const url = `/mobile/staff-team/repair-action/${repairActionId}/route`
    // 성공/실패 메시지는 호출부(saveRouteFromGps)에서 일괄 처리하므로 api()의 자동 토스트("저장되었습니다")를
    // 쓰지 않는다. noCustomApi 로 호출하고 성공 시에만 응답을 반환, 그 외에는 false 로 정규화한다.
    const { status, data } = await noCustomApi(url, 'patch', payload)
    return status === 200 ? { status, data } : false
  } catch (e) {
    window.__APP_LOG__('[repair] patchRepairActionRouteStaffTeam error:', e)
    return false
  }
}
